import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Box } from 'reflexbox';

import { useAuthenticatedUser, useFeature, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Container, Jumbo, Tabs, tokens } from '@treams/treams-ui';

import UserImageUpload from '../packages/kakapo-ui-application/pages/users/UserImageUpload';
import UserSettingsLanguage from '../packages/kakapo-ui-application/pages/users/UserSettingsLanguage';
import UserUpdateMissionForm from '../packages/kakapo-ui-application/pages/users/UserUpdateMissionForm';
import { ImageAspectRatio, ImageShape } from '../packages/kakapo-ui-kit/types/Image';
import { AIConfiguration } from './AIConfiguration';
import AccessToken from './AccessToken';
import PersonalNotificationSettings from './PersonalNotificationSettings';
import UserMfaConfiguration from './UserMfaConfiguration';

function UpdateMission() {
	const viewer = useAuthenticatedUser();
	const query = graphql.useUserQuery({ variables: { id: viewer.id } });

	if (!query.data) {
		return null;
	}

	return <UserUpdateMissionForm user={query.data.user as graphql.User} />;
}

export default function PersonalSettings() {
	const { t } = useTranslation();
	const viewer = useAuthenticatedUser();
	const hasMfa = useFeature(graphql.FeatureName.Mfa);
	const hasAI = useFeature(graphql.FeatureName.ConversationAiInsights);
	const { isWorkspaceSuperuser } = useViewerRoles();
	const canManageAccessToken = isWorkspaceSuperuser;

	return (
		<Box>
			<Jumbo
				title={t('personalSettings.title')}
				description={t('personalSettings.description')}
				footer={
					<Tabs.Container>
						<Tabs.Item path="/personal-settings" title={t('personalSettings.avatar.title')} />
						<Tabs.Item
							path="/personal-settings/mission"
							title={t('personalSettings.mission.title')}
						/>
						<Tabs.Item
							path="/personal-settings/language"
							title={t('personalSettings.language.title')}
						/>
						<Tabs.Item
							path="/personal-settings/notifications"
							title={t('personalSettings.notifications.title')}
						/>
						{hasMfa && (
							<Tabs.Item path="/personal-settings/mfa" title={t('personalSettings.mfa.title')} />
						)}
						{hasAI && (
							<Tabs.Item path="/personal-settings/ai" title={t('personalSettings.ai.title')} />
						)}
						{canManageAccessToken && (
							<Tabs.Item
								path="/personal-settings/access-token"
								title={t('personalSettings.accessToken.title')}
							/>
						)}
					</Tabs.Container>
				}
			/>
			<Switch>
				<Route exact path="/personal-settings">
					<Container pt={`${tokens.gap.page}px`} pb={4}>
						<UserImageUpload
							userId={viewer.id}
							imageSpecification={{
								title: 'avatar',
								key: 'avatars',
								aspectRatio: ImageAspectRatio.Square,
								shape: ImageShape.Circlular,
								allowedExtensions: ['jpg', 'jpeg', 'png'],
							}}
						/>
					</Container>
				</Route>
				<Route path="/personal-settings/mission">
					<Container pt={`${tokens.gap.page}px`} pb={4}>
						<UpdateMission />
					</Container>
				</Route>
				<Route path="/personal-settings/language">
					<Container pt={`${tokens.gap.page}px`} pb={4}>
						<UserSettingsLanguage />
					</Container>
				</Route>
				<Route path="/personal-settings/notifications">
					<PersonalNotificationSettings />
				</Route>
				{hasAI && (
					<Route path="/personal-settings/ai">
						<Container pt={`${tokens.gap.page}px`} pb={4}>
							<AIConfiguration />
						</Container>
					</Route>
				)}
				{hasMfa && (
					<Route path="/personal-settings/mfa">
						<UserMfaConfiguration />
					</Route>
				)}
				{canManageAccessToken && (
					<Route path="/personal-settings/access-token">
						<AccessToken />
					</Route>
				)}
				<Route path="*">
					<Redirect to="/personal-settings" />
				</Route>
			</Switch>
		</Box>
	);
}
